import Layout from "../../components/layout";
import Seo from "../../components/seo";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceCTA from "../../components/ServiceCTA";
import CTAButton from "../../components/CTAButton";
import { PortfolioCard, TestimonialHighlight } from "../../components/sections";
import onscriptLogo from "../../images/cases/onscript-logo.png";
import { useNavigate } from "react-router-dom";
import { FiBarChart2, FiClock, FiShield } from "react-icons/fi";
import { usePageMetadata } from "../../hooks/usePageMetadata";

const projects = [
  {
    title: "OnScript.ai",
    logo: onscriptLogo,
    summary:
      "Boost agent performance and maximize ROI with AI-powered speech analytics that scores every call with precision and consistency.",
    tags: ["Speech Analytics", "QA Automation", "Contact Center AI"],
    stats: [
      { label: "QA coverage", value: "100% calls evaluated" },
      { label: "Coaching speed", value: "Faster agent feedback" },
    ],
    href: "/portfolio/onscript-ai",
    externalHref: "https://onscript.ai",
  },
];

const testimonialData = [
  {
    quote:
      "Bercastle understood our revenue model from day one. They didn't just build features—they built a system that directly impacted our ARR growth and customer retention metrics.",
    name: "Rob Camhe",
    role: "CRO",
    company: "OnScript.ai",
  },
  {
    quote:
      "Every client interaction with our platform reflects Bercastle's attention to quality and user experience. Their work directly reduced our support tickets and increased customer satisfaction scores.",
    name: "Elissa Kassner",
    role: "Director of Client Success",
    company: "OnScript.ai",
  },
];

const PortfolioIndexPage = () => {
  const navigate = useNavigate();

  usePageMetadata({
    title: "Portfolio - Bercastle",
    description:
      "Selected work from Bercastle. AI-first case studies including OnScript.ai — generative operations platform.",
    pageType: "portfolio",
    content: {
      portfolio: {
        projects: projects.map((p) => ({
          name: p.title,
          summary: p.summary,
          url: p.href,
          external_url: p.externalHref,
          tags: p.tags,
          metrics: p.stats,
        })),
        testimonials: testimonialData,
      },
      meta: {
        source_url: "https://bercastle.com/portfolio",
      },
    },
  });

  return (
    <Layout classmain="portfolio-page">
      <Seo
        title="Portfolio - Bercastle"
        metaDesciption="Selected work from Bercastle. AI-first case studies including OnScript.ai — AI speech analytics for QA."
      />
      <Header />

      {/* Hero */}
      <section className="pt-48 pb-28 ">
        <div className="container-custom grid grid-cols-1 md:grid-cols-[1.35fr,0.85fr] gap-10 items-center">
          <div className="space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight text-slate-900">
              Projects that prove AI belongs in production
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              We build AI products that launch, scale, and earn trust. Explore the results and the guardrails behind them.
            </p>
            <div className="flex flex-wrap gap-4">
              <CTAButton onClick={() => navigate("/contact")}>Discuss your project</CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* Projects grid */}
      <section className="py-16 md:py-24">
        <div className="container-custom space-y-10">
          <div className="max-w-3xl">
            <p className="text-[#456EF2] font-montserrat font-semibold text-sm uppercase tracking-wide mb-3">
              Case studies
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Featured work</h2>
            <p className="text-lg text-slate-600">
              See how we deliver measurable results with production-grade AI systems and thoughtful product design.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8">
            {projects.map((project) => (
              <PortfolioCard key={project.title} {...project} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24">
        <div className="container-custom space-y-10">
          <div className="max-w-3xl">
            <p className="text-[#456EF2] font-montserrat font-semibold text-sm uppercase tracking-wide mb-3">
              Voices from clients
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What partners say</h2>
            <p className="text-lg text-slate-600">
              Collaboration, fast iterations, and accountable delivery are the constants across every engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonialData.map((testimonial) => (
              <TestimonialHighlight key={testimonial.name} {...testimonial} />
            ))}
          </div>
        </div>
      </section>

      <ServiceCTA
        title="Building your next AI product?"
        description="Tell us about the outcome you need. We'll map a path, prototype quickly, and ship reliably."
        buttonText="Start a project"
      />

      <Footer />
    </Layout>
  );
};

export default PortfolioIndexPage;
